import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TableRow, TableCell, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DiagnosisDoc } from '../../../components/interface/AssessmentInterface';
import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import IconButton, { ClearButton } from '../../../components/Button/iconbutton';
import TableComponent from '../../../components/Table/TableComponent';
import LoadingAnimation from '../../../components/LoadingAnimation';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import { apiHandler } from '../../../services/authService';



const DiagnosisForm: React.FC<{ patientId: string }> = ({ patientId }) => {
    const [diagnoses, setDiagnoses] = useState<DiagnosisDoc[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [editRecord, setEditRecord] = useState<DiagnosisDoc | null>(null);
    const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({ startDate: '', endDate: '' });
    const [, setDocumentSources] = useState<any[]>([]);
    const [hasSearched, setHasSearched] = useState(false); // New state variable

    useEffect(() => {
        fetchDiagnoses();

    }, [page, rowsPerPage, searchTerm, dateRange]); // Fetch on page and rows per page change

    const handleAddClick = async () => {
        setEditRecord(null);
        setOpenDialog(true);

        try {
            const payload = {
                slug: 'document-source',
                order_by: 'id',
                dir: 1,
            }
            const url        = `${BASE_URL}/resource/masters/all`;
            const response   = await apiHandler('', 'GET', payload, url);
    
            if (!response) {
                throw new Error("No response received from the server");
            }

            // const response = await axios.get(`${BASE_URL}/resource/masters/all`, {
            //     params: {
            //         slug: 'document-source',
            //         order_by: 'id',
            //         dir: 1,
            //     },
            //     headers: getAxiosConfig().headers
            // });

            console.log('Document Sources:', response.data);

            if (response.data.code === 200) {
                setDocumentSources(response.data.data.masters);
            } else {
                console.error('Error fetching document sources:', response.data.message);
            }
        } catch (error) {
            console.error('Error fetching document sources:', error);
        }
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
    };

    const fetchDiagnoses = async (startDate = '', endDate = '') => {
        setLoading(true);
        try {
            const response = await axios.get<{ code: number; message: string; data: { docs: DiagnosisDoc[]; pagination: { total: number; current_page: number; last_page: number; per_page: number } } }>(`${BASE_URL}/resource/docs`, {
                params: {
                    limit: rowsPerPage,
                    page: page + 1,
                    from: startDate,
                    to: endDate,
                    order_by: 'created_at',
                    dir: 2,
                    slug: 'icd',
                    user_id: patientId,
                    searchkey: searchTerm,
                },
                headers: getAxiosConfig().headers
            });

            console.log('API Response for Diagnoses:', response.data);

            if (response.data.code === 200) {
                const { docs, pagination } = response.data.data;
                setDiagnoses(docs);
                setTotalRecords(pagination.total);

                console.log('Mapped Diagnoses Data:', docs);
            } else {
                console.error('Error in response:', response.data.message);
            }
        } catch (error) {
            console.error('Error fetching diagnoses:', error);
        } finally {
            setLoading(false);
        }
    };

    const clearSearch = () => {

        setSearchTerm('');
        setDateRange({ startDate: '', endDate: '' });
        setHasSearched(false);
        fetchDiagnoses();
        setPage(0);
    };

    const deleteDiagnosis = async (id: number) => {
        try {
            const url       = `${BASE_URL}/resource/docs/${id}`;
            const response  = await apiHandler('', 'DELETE',{}, url);

            if (!response) {
                throw new Error("No response received from the server");
            }

            // const response = await axios.delete(`${BASE_URL}/resource/docs/${id}`, {
            //     headers: getAxiosConfig().headers
            // });

            if (response.data.code === 200) {
                setDiagnoses((prev) => prev.filter((diagnosis) => diagnosis.id !== id));
            } else {
                console.error('Error deleting diagnosis:', response.data.message);
            }
        } catch (error) {
            console.error('Error deleting diagnosis:', error);
        }
    };

    const handleDialogSave = (newRecord: DiagnosisDoc) => {
        setDiagnoses(prev => {
            if (editRecord) {
                return prev.map(record => (record.id === editRecord.id ? newRecord : record));
            } else {
                return [...prev, { ...newRecord, id: Date.now() }];
            }
        });
        fetchDiagnoses();
        setOpenDialog(false);
    };

    const handleEdit = (record: DiagnosisDoc) => {
        setEditRecord(record);
        setOpenDialog(true);
    };

    const handleDelete = (record: DiagnosisDoc) => {
        deleteDiagnosis(record.id);
    };



    const renderRow = (item: DiagnosisDoc, index: number) => {
        console.log('Rendering Row:', item);

        return (
            <TableRow key={item.id}>
                <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>
                    {item.addition_info.date}
                </TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.addition_info.title}</TableCell>
                <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem' }}>{item.addition_info.notes}</TableCell>

            </TableRow>
        );
    };

    return (
        <div>
            <div className="flex items-end justify-between w-full gap-12">
                <div className="flex flex-grow space-x-2 gap-2">
                    <DateRangePicker
                        startDateId="Diagnosis_Start_Date"
                        endDateId="Diagnosis_End_Date"
                        onChange={(dates: { startDate: string; endDate: string }) => {
                            setDateRange(dates)
                        }}
                        value={dateRange}
                    />


                    <TextField
                        type="text"
                        placeholder="Search ICD Code"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="p-2 rounded"
                        sx={{
                            backgroundColor: 'white',
                            '& .MuiOutlinedInput-root': {
                                height: 42,
                                borderRadius: '10px',
                                '& fieldset': {
                                    borderWidth: 1,
                                },
                                '&:hover fieldset': {
                                    borderColor: 'primary.dark',
                                },
                                '&.Mui-focused fieldset': {
                                },
                            },
                            '& .MuiInputBase-input': {
                                padding: '10px 12px',
                            },
                            '& .MuiInputLabel-root': {
                                transform: 'translate(14px, 12px) scale(1)',
                            },
                        }}
                    />
                    <IconButton icon={<SearchIcon />} onClick={() => fetchDiagnoses(dateRange.startDate, dateRange.endDate)} />
                    {/* Clear Icon Button */}
                    {hasSearched && (
                        <ClearButton onClick={clearSearch} />

                    )}
                </div>

            </div>
            <div className="relative">
                {loading ? (
                    <div className="text-center my-4">Loading...</div>
                ) : (
                    <div>
                        <TableComponent
                            headers={['S.No', 'Date', 'ICD Code', 'ICD Description']}
                            data={diagnoses}
                            loading={loading}
                            error={''}
                            renderRow={renderRow}
                        />

                    </div>
                )}
                {loading && (
                    <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-10">
                        <LoadingAnimation />
                    </div>
                )}
            </div>
            <TablePaginationComponent
                count={totalRecords}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_event, newPage) => {
                    setPage(newPage);
                    fetchDiagnoses(); // Fetch diagnoses for the new page
                }}
                onRowsPerPageChange={(event) => {
                    const newRowsPerPage = parseInt(event.target.value, 10);
                    setRowsPerPage(newRowsPerPage);
                    setPage(0); // Reset to first page
                    fetchDiagnoses(); // Fetch diagnoses with new rows per page
                }}
            />

        </div>
    );
};

export default DiagnosisForm;
